'use client';

import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import SearchMobileIcon from '@/components/ui/icons/search-mobile';
import ScanIcon from '@/components/ui/icons/scan';
import useAutoComplete from '@/hooks/algolia/useAutoComplete';
import SearchPopup from './search-popup';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useSearchParams } from 'next/navigation';

const placeholderWords = [
  'by Medicine Name',
  'by Product Name',
  'by Brand Name',
  'by Category Name',
];

const SearchBox = ({ fromSubMenu = false, cb, className = '' }) => {
  const t = useTranslations('common');
  const searchParams = useSearchParams();
  const locale = useLocale();
  const router = useRouter();

  // const trendingPopup = useRef(null);

  const {
    setAutocompleteState,
    handleQuerySelect,
    autocomplete,
    autocompleteState,
    popupRef,
  } = useAutoComplete();

  const pathname = usePathname();
  const isSearchPage = pathname === '/search';

  const handleSubmit = (e) => {
    e?.preventDefault?.();

    const trimmedValue = autocompleteState.query?.trim();
    if (trimmedValue && trimmedValue?.length >= 2) {
      lastPushedQueryRef.current = trimmedValue;
      router.push(`/search?q=${encodeURIComponent(trimmedValue)}`);
      cb?.();
    }
    autocomplete.setIsOpen(false);
  };

  const lastPushedQueryRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % placeholderWords.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isSearchPage) {
      setAutocompleteState({
        query: '',
        isOpen: false,
      });
      autocomplete.setQuery('');
      autocomplete.setIsOpen(false);
      lastPushedQueryRef.current = null;
    } else {
      const searchedQuery = searchParams.get('q') ?? '';
      if (searchedQuery !== lastPushedQueryRef.current) {
        setAutocompleteState((prev) => ({
          ...prev,
          query: searchedQuery,
        }));
        autocomplete.setQuery(searchedQuery);
        lastPushedQueryRef.current = searchedQuery;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isSearchPage, setAutocompleteState]);

  // const handlePushToSearchPage = (searchWord) => {
  //   router.push(`/search?q=${encodeURIComponent(searchWord)}`);
  //   cb?.();
  // };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className={`${!fromSubMenu && 'bg-tertiary-100'} relative flex flex-1 items-center justify-between rounded-[10px] py-2 max-lg:w-full max-sm:w-full lg:px-4 lg:py-2.5`}
      >
        {fromSubMenu ? (
          <div className={`mb-[11px] w-full px-3 ${className}`}>
            <div className='border-border flex h-[30px] w-full items-center justify-between gap-2 rounded-sm border px-3'>
              <button type='submit'>
                <SearchMobileIcon />
              </button>
              <input
                name='q'
                aria-label='Search for products'
                id='tw-search'
                placeholder={t('Search For Products')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                enterKeyHint='search'
                autoComplete='off'
                className='w-full py-10 text-[12px] outline-none placeholder:text-[10px]'
              />
              <ScanIcon />
            </div>
          </div>
        ) : (
          <>
            <div className='w-full max-sm:px-4'>
              {!autocompleteState.query && (
                <div
                  className={`pointer-events-none absolute top-1/2 px-4 text-xs lg:text-sm xl:text-base ${locale === 'ar' ? 'right-0' : 'left-0'} flex -translate-y-1/2 items-center gap-1 text-[#777B86] select-none`}
                >
                  {t('Search_by')}
                  <span className='w-32 overflow-hidden'>
                    <span
                      key={wordIndex}
                      className='animate-moveUp absolute top-0'
                    >
                      {t(placeholderWords[wordIndex])}
                    </span>
                  </span>
                </div>
              )}

              <input
                className='w-full pe-7 outline-none max-sm:placeholder:text-xs'
                aria-label='Search for products'
                {...(isSearchPage
                  ? {
                      value: autocompleteState?.query ?? '',
                      onChange: (e) => {
                        const newQuery = e.target.value;
                        setAutocompleteState((prev) => ({
                          ...prev,
                          query: newQuery,
                        }));
                        autocomplete.setQuery(newQuery);

                        clearTimeout(window.searchDebounce);
                        window.searchDebounce = setTimeout(() => {
                          const trimmed = newQuery.trim();
                          if (trimmed.length > 0) {
                            lastPushedQueryRef.current = newQuery;
                            router.push(
                              `/search?q=${encodeURIComponent(newQuery)}`
                            );
                          } else {
                            lastPushedQueryRef.current = '';
                            router.push('/');
                          }
                        }, 400);
                      },
                    }
                  : autocomplete.getInputProps({
                      onKeyDown: (e) => {
                        if (e.key === 'Enter') {
                          handleSubmit(e);
                        }
                      },
                    }))}
                value={autocompleteState?.query ?? ''}
              />

              {!isSearchPage && autocompleteState.isOpen && (
                <SearchPopup
                  ref={popupRef}
                  data={autocompleteState?.collections}
                  handleQuerySelect={handleQuerySelect}
                  query={autocompleteState?.query}
                />
              )}

              {/* {!isSearchPage &&
                !autocompleteState?.query &&
                !fromSubMenu && (
                  <TrendingPopup
                    t={t}
                    handlePushToSearchPage={handlePushToSearchPage}
                    ref={trendingPopup}
                  />
                )} */}
            </div>
            <button
              type='submit'
              aria-label={t('Search')}
              className='bg-primary absolute end-0 top-0 bottom-0 flex cursor-pointer items-center justify-center rounded-e-[10px] p-3 text-white'
            >
              <Image
                src='/search.gif'
                width={50}
                height={50}
                alt='search'
                className='h-[30px] w-[30px] bg-transparent text-transparent invert-100 lg:h-[35px] lg:w-[35px]'
              />
            </button>
          </>
        )}
      </form>
    </>
  );
};

export default SearchBox;
